import React, { useState, useEffect, useContext } from 'react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import {
  Button,
  Paragraph,
  Checkbox,
  ErrorMessage,
  Loader,
  ModalMultiUse,
  ModalOverlayWithoutClose,
  Headings,
  Link,
} from '@sentara-health/optimahealth_web';
import { PaperlessConsent } from 'templates';
import {
  DeviceTypes,
  useDeviceType,
  errorhandler,
  ANALYTICS_MESSAGES,
  domainForSegment,
  useAnalytics,
} from 'common';
import { usePreferenceUpdate } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from '../../context';
import { updatePreferenceHelper } from './updatePreferenceHelper';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';
import { PaperlessCommunicationsProps } from './interface';

function PaperlessCommunications({
  showPreferences,
  onClickCancel,
  handleSuccess,
  state,
  setState,
  onConsentChange,
}: Readonly<PaperlessCommunicationsProps>) {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(
    state?.paperlessConsent?.optInConsent || false
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [paperlessConsentDetails, setPaperlessConsentDetails] =
    useState<PaperlessConsent>({} as PaperlessConsent);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false); // Separate state for toast
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [checkboxValue, setCheckboxValue] = useState(
    state?.paperlessConsent?.optInConsent || false
  );

  const { trackContentListing } = useAnalytics(`${domainForSegment}/profile`);

  const [isTermsModal, setIsTermsModal] = useState<boolean>(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxValue(event.target.checked);
    setShowErrorMessage(!event.target.checked);
    if (!event.target.checked) {
      setIsScrolledToBottom(false);
    }
    if (onConsentChange) {
      onConsentChange(
        paperlessConsentDetails?.optInConsent || false,
        event.target.checked
      );
    }
  };
  const { fetchProfile } = usePreferenceSearchAPI();
  const { loginMemberId } = useContext(GlobalContext);
  const { updatePreference } = usePreferenceUpdate();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  const cancelConsent = () => {
    setPaperlessConsentDetails({ optInConsent: false });
    setCheckboxValue(false);
    setShowErrorMessage(false);
    setIsTermsModal(false);
    setIsScrolledToBottom(false);
    if (onConsentChange) {
      onConsentChange(false, false);
    }
  };

  const agreeConstent = () => {
    setPaperlessConsentDetails({ optInConsent: true });
    setCheckboxValue(true);
    setShowErrorMessage(false);
    setIsTermsModal(false);
    if (onConsentChange) {
      onConsentChange(true, true);
    }
  };

  useEffect(() => {
    setPaperlessConsentDetails(
      (state?.paperlessConsent as PaperlessConsent) ?? { optInConsent: false }
    );
  }, [state]);

  useEffect(() => {
    if (onConsentChange) {
      onConsentChange(
        paperlessConsentDetails?.optInConsent || false,
        checkboxValue
      );
    }
  }, [paperlessConsentDetails?.optInConsent, checkboxValue]);

  const YesForReceiveTextMsg = (
    <>
      <span>{t('profile.buttonLableOne')}</span>,{' '}
      {t('profile.wantPaperlessThisTime')}
    </>
  );
  const NoForReceiveTextMsg = (
    <>
      <span>{t('profile.buttonLableTwo')}</span>,{' '}
      {t('profile.notPaperlessThisTime')}
    </>
  );

  const EOBListContent = (
    <>
      {t('profile.paperlessIncludes')}{' '}
      <span>{t('profile.paperlessIncludesBold')}</span>{' '}
      {t('profile.paperlessAvailable')}
    </>
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const remainingScrollDistance =
      e.currentTarget.scrollHeight -
      e.currentTarget.scrollTop -
      e.currentTarget.clientHeight;
    const bottom = remainingScrollDistance <= 1;
    setIsScrolledToBottom(prev => prev || bottom);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!checkboxValue && paperlessConsentDetails?.optInConsent) {
      setShowErrorMessage(true);
    } else {
      handleEmailConsent(
        event as unknown as React.MouseEvent<HTMLButtonElement>
      );
    }
  };

  const handleEmailConsent = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setLoading(true);
    const apiPayload = updatePreferenceHelper({
      loginMemberId,
      status: paperlessConsentDetails?.optInConsent ? 'Opt-in' : 'Opt-out',
    });
    const response = await updatePreference(apiPayload);
    if (response?.errorCode) {
      errorhandler(response?.errorCode);
      trackContentListing('617', ANALYTICS_MESSAGES.PAPERLESS_SAVE_FAILURE, 20);
    } else {
      if (paperlessConsentDetails?.optInConsent) {
        trackContentListing(
          '615',
          ANALYTICS_MESSAGES.PAPERLESS_CLICK_OPTIN,
          18
        );
        handleSuccess(
          paperlessConsentDetails?.optInConsent ? 'success' : 'failure'
        );
        closeConfirmSignUp(event);
      } else {
        // No paperless consent, proceed with closing the confirmation modal
        closeConfirmSignUp(event);
      }
    }
    fetchProfile({
      onSuccess: () => {
        setLoading(false);
      },
    });
  };

  // To close the email update confirmation modal
  function cancelEmailAlert() {
    trackContentListing('618', ANALYTICS_MESSAGES.PAPERLESS_CANCEL, 21);
    setShowConfirmModal(false);
  }

  // To close the email paperless confirmation modal and to display the success message
  function closeConfirmSignUp(e: React.MouseEvent<HTMLButtonElement>) {
    onClickCancel(e);
    setState({ ...state, paperlessConsent: paperlessConsentDetails });
  }

  // Effect to hide the toast after 4 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (showToast) {
      timer = setTimeout(() => {
        setShowToast(false);
      }, 6000);
    }
    return () => clearTimeout(timer);
  }, [showToast]);

  return (
    <>
      {loading && <Loader />}
      <form onSubmit={onSubmit}>
        <div className="col-12 col-lg-9">
          <div className="row">
            <Paragraph
              text={EOBListContent}
              variant="regular"
              data-testId="EOBListContent"
              className={'mb-4'}
            />
            <div className="row">
              <div className="col-md-5 col-12">
                <Checkbox
                  id="emailNotificationYes"
                  className="form-check-input mt-0"
                  name="emailNotification"
                  type="radio"
                  dataTestId="emailNotificationYes"
                  checked={paperlessConsentDetails?.optInConsent}
                  onChange={() => {
                    setPaperlessConsentDetails({ optInConsent: true });
                    setCheckboxValue(false);
                    setShowErrorMessage(false);
                    if (onConsentChange) {
                      onConsentChange(true, false);
                    }
                  }}
                >
                  {YesForReceiveTextMsg}
                </Checkbox>
                <Paragraph
                  level={1}
                  text={t('profile.yesRadioContent')}
                  variant="regular"
                  data-testId="yesRadioContent"
                  className="lh-sm mt-2 mb-3 pl-2"
                />
              </div>
              <div className="col-md-6 col-12 mb-3 d-flex">
                <Checkbox
                  id="emailNotificationNo"
                  className="my-4"
                  name="emailNotification"
                  type="radio"
                  dataTestId="emailNotificationNo"
                  checked={!paperlessConsentDetails?.optInConsent}
                  onChange={() => {
                    setPaperlessConsentDetails({ optInConsent: false });
                    setShowConfirmModal(true);
                    setShowErrorMessage(false);
                    setCheckboxValue(false);
                    if (onConsentChange) {
                      onConsentChange(false, false);
                    }
                  }}
                >
                  {NoForReceiveTextMsg}
                </Checkbox>
              </div>
            </div>
            {paperlessConsentDetails?.optInConsent && (
              <>
                {showPreferences ? null : (
                  <>
                    <Paragraph
                      text={t('profile.paperlessAcknowledgement')}
                      variant="regular"
                      data-testId="paperlessAcknowledgement"
                    />
                    <div
                      className="phone_notify_msg_wrapper notification_border mx-2 mb-3"
                      data-testid="peperLessTermsandCondition"
                      onScroll={handleScroll}
                    >
                      <div className="d-flex flex-column">
                        <Paragraph
                          text={
                            <Trans
                              i18nKey="profile.paperlessTextarea"
                              components={{
                                strong: <strong />,
                                link1: (
                                  <Link
                                    to={t('profile.paperlessTextareaUrl')}
                                    title="Members Url"
                                    target="_blank"
                                  />
                                ),
                              }}
                            />
                          }
                          variant="regular"
                          data-testId="paperlessTextarea"
                          className="lineBrake mb-0"
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="row">
                  <div
                    className={`col-12 d-flex align-items-center ${
                      showErrorMessage ? '' : 'mb-3'
                    }`}
                  >
                    <Checkbox
                      id="termsAndCondition"
                      checked={checkboxValue}
                      className={`mt-1 mb-5 ${
                        showErrorMessage ? 'error-input-bdr' : ''
                      }`}
                      onChange={handleCheckboxChange}
                      type="checkbox"
                      dataTestId="termsAndCondition"
                    />
                    <Paragraph
                      level={2}
                      text={
                        <Trans
                          i18nKey={
                            showPreferences
                              ? 'communications.paperlessTermsOfUse'
                              : 'profile.termsOfUseProfile'
                          }
                          components={{
                            link1: (
                              <Link
                                onClick={() => setIsTermsModal(true)}
                                className="text-decoration-underline small-link"
                                data-testid="termsOfUseLink"
                              >
                                {t('common.termsOfUse')}
                              </Link>
                            ),
                          }}
                        />
                      }
                      disable={!showPreferences && !isScrolledToBottom}
                      variant="regular"
                      data-testId="termsOfUseDescription"
                      className="mb-0 ms-2"
                    />
                  </div>
                  {showErrorMessage && (
                    <div className="col-12 mt-1 mb-3">
                      <ErrorMessage>{t('profile.goPaperConsent')}</ErrorMessage>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {showPreferences ? null : (
            <>
              <hr />
              <div className="row">
                <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                  <Button
                    dataTestId={'saveButton'}
                    variant="primary_btn"
                    disabled={
                      paperlessConsentDetails?.optInConsent && !checkboxValue
                    }
                    type={'submit'}
                  >
                    {t('common.save')}
                  </Button>
                </div>
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    type="button"
                    dataTestId={'cancelButtonPaperless'}
                    variant="secondary_btn"
                    onClick={onClickCancel}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </form>

      {/*Confirm modal for opting out of paperless communications */}
      <ModalMultiUse
        header={t('profile.amSurePopUpTitle')}
        body={
          <>
            <Paragraph
              text={t('profile.iamSureContent')}
              variant="regular"
              data-testId="iamSureContent"
              className="mb-0"
            />
          </>
        }
        footer={
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId={'imSureButton'}
                variant="primary_btn"
                className=" btn-md-height w-100"
                type="submit"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPaperlessConsentDetails({ optInConsent: true });
                  setShowErrorMessage(false);
                  setCheckboxValue(false);
                  trackContentListing(
                    '615',
                    ANALYTICS_MESSAGES.POP_PAPERLESS_COMMUNICATION_OPT_IN,
                    18,
                    `${domainForSegment}${globalThis.location.pathname}`
                  );
                }}
              >
                {t('profile.paperlessKeepButton')}
              </Button>
            </div>

            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId={'signupButton'}
                variant="secondary_btn"
                className="btn-md-height w-100"
                onClick={() => {
                  cancelEmailAlert();
                  setCheckboxValue(false);
                  trackContentListing(
                    '616',
                    ANALYTICS_MESSAGES.POP_PAPERLESS_COMMUNICATION_OPT_OUT,
                    19,
                    `${domainForSegment}${globalThis.location.pathname}`
                  );
                }}
              >
                {t('profile.paperlessTurnOffButton')}
              </Button>
            </div>
          </>
        }
        variant="defaultNoPyBody"
        isMobile={isMobile}
        isOpen={showConfirmModal}
        onClose={cancelEmailAlert}
        data-testid="amSurePopUp"
      />

      {/* Terms and conditions modal */}
      <ModalOverlayWithoutClose
        isOpen={isTermsModal}
        onClose={() => {
          setIsTermsModal(false);
          setIsScrolledToBottom(false);
        }}
        overlayInfo="Modal information"
        className="isTermsModal"
        data-testid="termsAndConditionsModal"
      >
        <div className="modal-header border-bottom-1 gap-2">
          {isMobile ? (
            <Paragraph
              level={3}
              text={t('profile.paperlessCommunicationsTermsModalTitle')}
              className="modal-title"
              variant="medium"
            />
          ) : (
            <Headings
              text={t('profile.paperlessCommunicationsTermsModalTitle')}
              className="modal-title"
              variant="semiBold"
            />
          )}
          <button
            className="modal-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              setIsTermsModal(false);
              setIsScrolledToBottom(false);
            }}
          />
        </div>
        <div className="modal-body pt-4 pb-5" onScroll={handleScroll}>
          <Paragraph
            text={
              <Trans
                i18nKey="profile.paperlessTextarea"
                components={{
                  strong: <strong />,
                  link1: (
                    <Link
                      to={t('profile.paperlessTextareaUrl')}
                      title="Members Url"
                      target="_blank"
                    />
                  ),
                }}
              />
            }
            variant="regular"
            data-testId="paperlessTextarea"
            className="lineBrake mb-0"
          />
        </div>
        <div className="modal-footer border-top-1 gap-3">
          <div className="row">
            <div className="col-sm-auto col-12 mb-sm-0 mb-3">
              <Button
                dataTestId={'modalAgreeButton'}
                variant="primary_btn"
                type="submit"
                className="btn-md-height"
                disabled={!isScrolledToBottom}
                onClick={() => {
                  agreeConstent();
                }}
              >
                {t('profile.agreeTerms')}
              </Button>
            </div>
            <div className="col-sm-auto col-12">
              <Button
                dataTestId={'modalCancelButton'}
                variant="secondary_btn"
                type="submit"
                className="btn-md-width btn-md-height"
                onClick={() => {
                  cancelConsent();
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      </ModalOverlayWithoutClose>
    </>
  );
}

export default PaperlessCommunications;
